import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import BrandLogo from "../components/BrandLogo";
import DOMPurify from "dompurify";
import { api } from "../lib/api";
import { fmtDate } from "../lib/format";
import { openCookiePreferences } from "../lib/analytics";

export function LegalLinks({ className = "hover:text-slate-900" }) {
  return (
    <>
      <Link to="/privacy" data-testid="footer-privacy-link" className={className}>Privacy Policy</Link>
      <Link to="/cookie-policy" data-testid="footer-cookie-link" className={className}>Cookie Policy</Link>
      <Link to="/terms" data-testid="footer-terms-link" className={className}>Termini e Condizioni</Link>
      <button type="button" data-testid="footer-cookie-prefs" onClick={openCookiePreferences} className={className}>Preferenze cookie</button>
    </>
  );
}

function LegalLayout({ title, updated, children }) {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-card">
      <header className="sticky top-0 z-20 bg-background/80 backdrop-blur border-b border-slate-100">
        <div className="max-w-3xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <BrandLogo />
          </Link>
          <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900">
            <ArrowLeft style={{ width: 16, height: 16 }} /> Indietro
          </button>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-5 py-12" data-testid="legal-page">
        <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">{title}</h1>
        {updated && <p className="text-sm text-slate-400 mt-2">Ultimo aggiornamento: {updated}</p>}
        <div className="prose prose-slate mt-8 space-y-5 text-slate-600 leading-relaxed text-sm">{children}</div>
        <footer className="mt-12 pt-6 border-t border-slate-100 flex flex-wrap gap-5 text-sm text-slate-500">
          <LegalLinks />
        </footer>
      </main>
    </div>
  );
}

function H({ children }) { return <h2 className="text-base font-bold text-slate-900 !mt-8">{children}</h2>; }

function Placeholder({ kind }) {
  return (
    <div data-testid={`legal-${kind}-placeholder`} className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-6 text-slate-500">
      <p className="font-semibold text-slate-700">Contenuto in preparazione</p>
      <p className="mt-2">Il testo di questa pagina sarà pubblicato a breve. Per qualsiasi domanda puoi contattarci tramite la chat di assistenza.</p>
    </div>
  );
}

function EditableLegalPage({ kind, title }) {
  const [page, setPage] = useState(null);
  useEffect(() => {
    api.get(`/public/legal/${kind}`).then(({ data }) => setPage(data)).catch(() => setPage({ html: "" }));
  }, [kind]);
  return (
    <LegalLayout title={title} updated={page?.html && page.updated_at ? fmtDate(page.updated_at) : null}>
      {!page ? (
        <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : page.html ? (
        <div data-testid={`legal-${kind}-content`} className="qf-rich" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(page.html) }} />
      ) : <Placeholder kind={kind} />}
    </LegalLayout>
  );
}

export function LegalFooter({ className = "" }) {
  return (
    <footer data-testid="legal-footer" className={`flex flex-wrap justify-center gap-x-5 gap-y-2 text-xs text-slate-400 ${className}`}>
      <LegalLinks className="text-slate-400 hover:text-slate-700 transition-colors" />
    </footer>
  );
}

export function Terms() { return <EditableLegalPage kind="terms" title="Termini e Condizioni" />; }

function IubendaEmbed({ href, title }) {
  useEffect(() => {
    document.getElementById("iubenda-embed-loader")?.remove();
    const s = document.createElement("script");
    s.id = "iubenda-embed-loader";
    s.src = "https://cdn.iubenda.com/iubenda.js";
    document.body.appendChild(s);
  }, [href]);
  return (
    <div data-testid="iubenda-embed">
      <a href={href} className="iubenda-white iubenda-noiframe iubenda-embed iub-body-embed" title={title}>{title}</a>
    </div>
  );
}

export function Privacy() {
  return (
    <LegalLayout title="Privacy Policy">
      <IubendaEmbed href="https://www.iubenda.com/privacy-policy/38920380" title="Privacy Policy" />
    </LegalLayout>
  );
}

export function CookiePolicy() {
  return (
    <LegalLayout title="Cookie Policy">
      <IubendaEmbed href="https://www.iubenda.com/privacy-policy/38920380/cookie-policy" title="Cookie Policy" />
    </LegalLayout>
  );
}

export function Dpa() {
  return (
    <LegalLayout title="Accordo sul Trattamento dei Dati (DPA — Art. 28 GDPR)" updated="Giugno 2026">
      <p>Il presente Accordo (Data Processing Agreement) disciplina il trattamento dei dati personali dei clienti finali che l'utente professionista ("Titolare") inserisce nei preventivi e che MyQuoteFlow ("Responsabile") tratta per suo conto, ai sensi dell'art. 28 GDPR.</p>
      <H>1. Ruoli</H>
      <p>L'utente professionista è Titolare del trattamento dei dati dei propri clienti. MyQuoteFlow è nominato Responsabile del trattamento e tratta tali dati solo su istruzione documentata del Titolare.</p>
      <H>2. Oggetto e durata</H>
      <p>Il trattamento ha ad oggetto i dati dei clienti (anagrafica, P.IVA/CF, email, contenuti dei preventivi, dati di firma) per la durata del rapporto contrattuale.</p>
      <H>3. Obblighi del Responsabile</H>
      <p>MyQuoteFlow garantisce riservatezza, misure di sicurezza adeguate (art. 32), assistenza al Titolare per l'esercizio dei diritti degli interessati e per le violazioni di dati, e la cancellazione/restituzione dei dati al termine del servizio.</p>
      <H>4. Sub-responsabili</H>
      <p>MyQuoteFlow può avvalersi di fornitori di hosting e servizi tecnici quali sub-responsabili, vincolati da obblighi equivalenti di protezione dei dati.</p>
      <H>5. Misure di sicurezza</H>
      <p>Cifratura delle password, trasmissione protetta, controllo accessi, tracciamento audit delle firme e integrità documentale tramite hash SHA-256.</p>
      <H>6. Cancellazione</H>
      <p>Su richiesta del Titolare o alla cessazione del servizio, MyQuoteFlow cancella definitivamente i dati trattati per conto del Titolare, salvo obblighi di conservazione di legge.</p>
    </LegalLayout>
  );
}
